<template>
  <div ref="root" class="day-lens" :class="{ 'day-lens-sheet': sheet }" tabindex="-1" role="dialog" :aria-label="title" @keydown.esc.stop="emit('close')">
    <div class="day-lens-header">
      <button type="button" class="btn btn-default btn-xs" :title="_('Previous')" @click="emit('navigate', -1)"><i class="fa fa-arrow-left"></i></button>
      <span class="day-lens-title">{{ title }}</span>
      <button type="button" class="btn btn-default btn-xs" :title="_('Next')" @click="emit('navigate', 1)"><i class="fa fa-arrow-right"></i></button>
      <button type="button" class="btn btn-default btn-xs day-lens-close" :title="_('Close')" @click="emit('close')"><i class="fa fa-times"></i></button>
    </div>
    <TimelineView
      :url="url"
      :products="source.products"
      :resources="source.resources"
      :resourcePools="source.resourcePools"
      mode="hourly"
      :embedded="range"
      :fixedHeight="sheet ? sheetBodyHeight : null"
      :shopId="shopId"
      :locale="locale"
      :component="component"
      :column="column"
      :baseDomain="baseDomain"
      :apiKey="apiKey"
      :reloadEvents="reloadEvents"
      :keepTimes="keepTimes"
      :embeddedLabelWidth="labelWidth"
      :embeddedDensity="density"
      v-on:content-height="contentHeightChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const HEADER_HEIGHT = 44

const props = withDefaults(defineProps<{
  source: { products?: (string | number)[], resources?: (string | number)[], resourcePools?: (string | number)[] }
  title: string
  range: { start: Date, end: Date }
  shopId: string
  locale?: string
  component: any
  column?: any
  baseDomain: string
  apiKey: string
  reloadEvents?: string[]
  keepTimes?: boolean
  labelWidth?: number
  density?: 'comfortable' | 'compact' | 'relaxed'
  sheet?: boolean
}>(), {
  locale: undefined,
  column: undefined,
  reloadEvents: undefined,
  keepTimes: false,
  labelWidth: 400,
  density: 'comfortable',
  sheet: false
})

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'navigate', delta: number): void
  (e: 'height', height: number): void
}>()

const { $translate } = useNuxtApp()
const _ = $translate as (key: string) => string

const root = ref<HTMLElement | null>(null)
const viewportHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 600)
const sheetBodyHeight = computed(() => Math.max(200, viewportHeight.value - HEADER_HEIGHT))

const url = computed(() => {
  const { products, resources, resourcePools } = props.source
  const query = products && products.length
    ? 'products=' + products.join(',')
    : 'resourcepools=' + (resourcePools || []).join(',') + '&resources=' + (resources || []).join(',')
  return props.baseDomain + props.shopId + '/schedule.json?' + query + '&apikey=' + props.apiKey + '&hourly=true&format=band'
})

const contentHeightChanged = (height: number) => {
  emit('height', HEADER_HEIGHT + height + 2)
}

const onResize = () => {
  viewportHeight.value = window.innerHeight
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  // Focus so Esc closes the lens, and so the inline lens is reachable by keyboard
  root.value?.focus()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style scoped>
.day-lens {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow-anchor: none;
  background: #fff;
  border: 1px solid #c9d6e2;
  border-left: 3px solid #3499db;
  box-shadow: inset 0 6px 8px -8px rgba(15, 23, 42, 0.35);
  outline: none;
}
.day-lens-header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 6px;
  height: 44px;
  padding: 0 8px;
  background: #f3f7fb;
  border-bottom: 1px solid #e3e7ec;
}
.day-lens-header .btn-xs {
  height: 32px;
  min-width: 32px;
  border-radius: 6px;
}
.day-lens-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 700;
  font-size: 13px;
}
.day-lens-close {
  margin-left: auto;
}
.day-lens-sheet {
  position: fixed;
  inset: 0;
  z-index: 1050;
  border: 0;
  box-shadow: none;
}
</style>
